import type { CameraConfig } from './types';
import type { Maybe } from '@/src/types';
import type { DoubleKeyRecord } from '@/src/utils/doubleKeyRecord';

import { defineStore } from 'pinia';
import { reactive } from 'vue';
import z from 'zod';

import {
  deleteSecondKey,
  doubleKeySchema,
  getDoubleKeyRecord,
  patchDoubleKeyRecord,
} from '@/src/utils/doubleKeyRecord';

import { CameraConfigSchema } from './types';

export const SerializedViewCameraStoreSchema = doubleKeySchema(CameraConfigSchema);
export type SerializedViewCameraStore = z.infer<typeof SerializedViewCameraStoreSchema>;

export const useViewCameraStore = defineStore('viewCamera', () => {
  const configs = reactive<SerializedViewCameraStore>({});

  const getConfig = (viewID: Maybe<string>, dataID: Maybe<string>) =>
    getDoubleKeyRecord(configs, viewID, dataID);

  const updateConfig = (
    viewID: string,
    dataID: string,
    patch: Partial<CameraConfig>,
  ) => {
    const config = {
      ...getConfig(viewID, dataID),
      ...patch,
    };

    patchDoubleKeyRecord(configs, viewID, dataID, config);
  };

  const removeView = (viewID: string) => {
    delete configs[viewID];
  };

  const removeData = (dataID: string, viewID?: string) => {
    if (viewID) {
      delete configs[viewID]?.[dataID];
    }
    else {
      deleteSecondKey(configs, dataID);
    }
  };

  const serialize = (): SerializedViewCameraStore =>
    JSON.parse(JSON.stringify(configs)) as SerializedViewCameraStore;

  const deserialize = (serialized?: DoubleKeyRecord<CameraConfig>) => {
    if (!serialized) return;
    Object.entries(serialized).forEach(([viewID, byData]) => {
      Object.entries(byData).forEach(([dataID, config]) => {
        patchDoubleKeyRecord(configs, viewID, dataID, config);
      });
    });
  };

  return {
    configs,
    getConfig,
    updateConfig,
    removeView,
    removeData,
    serialize,
    deserialize,
  };
});

export default useViewCameraStore;
